// Agent sessions page - shows all agent sessions across all projects

import {
    apiRequest,
    initAuthenticatedPage,
    showLoading,
    showError,
} from "./common";

interface AgentSession {
    id: number;
    ticket_id: number;
    project_id: number;
    session_type: "planning" | "implementation" | "update_pr" | "review_pr" | "evaluate_pr_review";
    tool: "claude_code" | "codex";
    model: "claude" | "glm" | "codex_default";
    model_name: string | null;
    reasoning_effort: string | null;
    auth_method: "oauth" | "api_key" | null;
    status:
        | "pending"
        | "initializing"
        | "running"
        | "completed"
        | "failed"
        | "cancelled";
    container_id: string | null;
    worktree_path: string | null;
    branch_name: string | null;
    log_file_path: string | null;
    error_message: string | null;
    pr_number: number | null;
    pr_url: string | null;
    source_pr_number: number | null;
    source_branch_name: string | null;
    parent_session_id: number | null;
    flow_name: string | null;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
    updated_at: string;
}

interface AgentSessionMetrics {
    id: number;
    session_id: number;
    total_cost_usd: number | null;
    input_tokens: number | null;
    output_tokens: number | null;
    cache_read_tokens: number | null;
    cache_creation_tokens: number | null;
    active_time_seconds: number | null;
    tool_calls_total: number | null;
    tool_calls_success: number | null;
    tool_calls_failed: number | null;
    api_requests_total: number | null;
    api_errors_total: number | null;
    lines_of_code_modified: number | null;
    collected_at: string;
}

let sessions: AgentSession[] = [];
const sessionMetrics: Map<number, AgentSessionMetrics> = new Map();
let currentLogsSessionId: number | null = null;

let sessionsContainer: HTMLElement;
let agentLogsDialog: HTMLDialogElement;
let agentLogsContent: HTMLPreElement;

function escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

async function initAgentSessionsPage(): Promise<void> {
    const user = await initAuthenticatedPage();
    if (!user) {
        return;
    }

    sessionsContainer = document.getElementById(
        "sessions-container"
    ) as HTMLElement;
    agentLogsDialog = document.getElementById(
        "agent-logs-dialog"
    ) as HTMLDialogElement;
    agentLogsContent = document.getElementById(
        "agent-logs-content"
    ) as HTMLPreElement;

    // Logs dialog event listeners
    document
        .getElementById("close-logs-btn")
        ?.addEventListener("click", () => agentLogsDialog.close());
    document
        .getElementById("logs-close-btn")
        ?.addEventListener("click", () => agentLogsDialog.close());
    document
        .getElementById("logs-refresh-btn")
        ?.addEventListener("click", () => {
            if (currentLogsSessionId) {
                const session = sessions.find(
                    (s) => s.id === currentLogsSessionId
                );
                if (session) {
                    refreshLogs(
                        currentLogsSessionId,
                        session.project_id,
                        session.ticket_id
                    );
                }
            }
        });
    agentLogsDialog.addEventListener("click", (e) => {
        if (e.target === agentLogsDialog) agentLogsDialog.close();
    });

    loadSessions();
}

async function loadSessions(): Promise<void> {
    showLoading(sessionsContainer);

    try {
        sessions = await apiRequest<AgentSession[]>("/agent-sessions");

        // Fetch metrics for completed/failed sessions in parallel
        const sessionsWithMetrics = sessions.filter(
            (s) => s.status === "completed" || s.status === "failed"
        );
        await loadSessionMetrics(sessionsWithMetrics);

        renderSessions();
    } catch (error) {
        showError(
            error instanceof Error ? error.message : "Failed to load sessions",
            sessionsContainer
        );
    }
}

async function loadSessionMetrics(sessionsToLoad: AgentSession[]): Promise<void> {
    if (sessionsToLoad.length === 0) return;

    const metricsPromises = sessionsToLoad.map(async (session) => {
        try {
            const metrics = await apiRequest<AgentSessionMetrics>(
                `/projects/${session.project_id}/tickets/${session.ticket_id}/agent-sessions/${session.id}/metrics`
            );
            return { sessionId: session.id, metrics };
        } catch {
            // Metrics not available for this session
            return null;
        }
    });

    const results = await Promise.all(metricsPromises);
    sessionMetrics.clear();
    for (const result of results) {
        if (result) {
            sessionMetrics.set(result.sessionId, result.metrics);
        }
    }
}

function formatMetricsCell(sessionId: number): string {
    const metrics = sessionMetrics.get(sessionId);
    if (!metrics) return "-";

    const parts: string[] = [];

    // Cost
    if (metrics.total_cost_usd !== null) {
        parts.push(`$${metrics.total_cost_usd.toFixed(4)}`);
    }

    // Tokens (input + output)
    if (metrics.input_tokens !== null || metrics.output_tokens !== null) {
        const input = metrics.input_tokens ?? 0;
        const output = metrics.output_tokens ?? 0;
        const total = input + output;
        parts.push(`${(total / 1000).toFixed(1)}k tok`);
    }

    // Active time
    if (metrics.active_time_seconds !== null) {
        const mins = Math.floor(metrics.active_time_seconds / 60);
        const secs = Math.round(metrics.active_time_seconds % 60);
        const timeStr = mins > 0 ? `${mins}m${secs}s` : `${secs}s`;
        parts.push(timeStr);
    }

    if (parts.length === 0) return "-";

    return `<span class="metrics-compact">${parts.join(" | ")}</span>`;
}

function renderSessions(): void {
    if (sessions.length === 0) {
        sessionsContainer.innerHTML =
            '<p class="muted">No agent sessions yet.</p>';
        return;
    }

    sessionsContainer.innerHTML = `
        <table class="sessions-table">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Ticket</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Metrics</th>
                    <th>Branch</th>
                    <th>PR</th>
                    <th>Created</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                ${sessions
                    .map(
                        (session) => `
                    <tr>
                        <td>${session.id}</td>
                        <td>
                            <a href="/pages/ticket-view.html?projectId=${session.project_id}&ticketId=${session.ticket_id}">
                                #${session.ticket_id}
                            </a>
                        </td>
                        <td>
                            <span class="session-type-badge session-type-${session.session_type}">
                                ${session.session_type}
                            </span>
                        </td>
                        <td>
                            <span class="agent-status-badge agent-status-${session.status}">
                                ${session.status.replace("_", " ")}
                            </span>
                        </td>
                        <td>
                            ${formatMetricsCell(session.id)}
                        </td>
                        <td>
                            ${session.branch_name ? `<code class="branch-name">${escapeHtml(session.branch_name)}</code>` : "-"}
                        </td>
                        <td>
                            ${session.pr_url ? `<a href="${escapeHtml(session.pr_url)}" target="_blank">#${session.pr_number}</a>` : "-"}
                        </td>
                        <td>${new Date(session.created_at).toLocaleString()}</td>
                        <td>
                            <button type="button" class="outline small view-logs-btn" data-session-id="${session.id}" data-project-id="${session.project_id}" data-ticket-id="${session.ticket_id}">
                                Logs
                            </button>
                        </td>
                    </tr>
                `
                    )
                    .join("")}
            </tbody>
        </table>
    `;

    // Attach event listeners
    sessionsContainer.querySelectorAll(".view-logs-btn").forEach((btn) => {
        btn.addEventListener("click", () => {
            const el = btn as HTMLElement;
            const sessionId = parseInt(el.dataset.sessionId || "0", 10);
            const projectId = parseInt(el.dataset.projectId || "0", 10);
            const ticketId = parseInt(el.dataset.ticketId || "0", 10);
            viewSessionLogs(sessionId, projectId, ticketId);
        });
    });
}

async function viewSessionLogs(
    sessionId: number,
    projectId: number,
    ticketId: number
): Promise<void> {
    currentLogsSessionId = sessionId;
    agentLogsContent.textContent = "Loading...";
    agentLogsDialog.showModal();

    await refreshLogs(sessionId, projectId, ticketId);
}

async function refreshLogs(
    sessionId: number,
    projectId: number,
    ticketId: number
): Promise<void> {
    try {
        const response = await apiRequest<{
            session_id: number;
            logs: string;
            is_complete: boolean;
        }>(
            `/projects/${projectId}/tickets/${ticketId}/agent-sessions/${sessionId}/logs`
        );
        agentLogsContent.textContent = response.logs || "(no logs yet)";
    } catch (error) {
        agentLogsContent.textContent = `Error loading logs: ${error instanceof Error ? error.message : "Unknown error"}`;
    }
}

initAgentSessionsPage();
